<template>
  <div class="search-origin">
    <Input
      id="originStation"
      :label="$t('travelPlanner.search.origin')"
      :internalLabel="true"
      v-model="inputValue"
      :hasDelete="true"
      :placeholder="$t('travelPlanner.search.origin')"
      :icon="compass"
      :suggestions="filteredStations"
      @focus="onInputFocus"
      @select-suggestion="handleSelectSuggestion"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, watch } from 'vue'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useMyTravelStore } from '@/stores/myTravelStore'
  import { fetchStations } from '@/services/journeyService'
  import { Station } from '@/types/stationTypes'
  import Input from '@/components/BaseComponents/Input.vue'
  import compass from '@/assets/icons/compass.svg'

  export default defineComponent({
    name: 'SearchOrigin',
    components: {
      Input,
    },
    setup() {
      const journeyStore = useJourneyStore()
      const myTravelStore = useMyTravelStore()
      const stations = computed(() => journeyStore.allStations)
      const selectedStation = ref<Station | null>(null)
      const inputValue = ref('')

      const onInputFocus = async () => {
        try {
          // Only fetch stations if not already loaded
          if (!stations.value.length) {
            const fetchedStations = await fetchStations()
            journeyStore.setAllStations(fetchedStations)
          }
          
          journeyStore.setDestinationStation({
            name: '',
            _u_i_c_station_code: '',
          })
        } catch (error) {
          console.error('Error fetching stations:', error)
        }
      }

      const handleSelectSuggestion = (stationCode: string) => {
        const station = stations.value.find(
          (station) => station._u_i_c_station_code === stationCode
        )
        if (station) {
          journeyStore.setOriginStation({
            name: station.name,
            _u_i_c_station_code: station._u_i_c_station_code,
          })
          myTravelStore.setOriginDestination(station.name, myTravelStore.destination)
          selectedStation.value = station
          inputValue.value = station.name
        }
      }

      const filteredStations = computed(() => {
        if (!inputValue.value) return []
        return stations.value
          .filter((station) =>
            station.name.toLowerCase().includes(inputValue.value.toLowerCase())
          )
          .map((station) => ({
            name: station.name,
            code: station._u_i_c_station_code,
          }))
      })

      watch(
        () => journeyStore.OriginStation,
        (newOriginStation) => {
          inputValue.value = newOriginStation.name
          myTravelStore.setOriginDestination(newOriginStation.name, myTravelStore.destination)
        },
        { immediate: true }
      )

      watch(
        () => inputValue.value,
        (newValue) => {
          if (!newValue) {
            journeyStore.setOriginStation({
              name: '',
              _u_i_c_station_code: '',
            })
            myTravelStore.setOriginDestination('', myTravelStore.destination)
            selectedStation.value = null
          }
        }
      )

      return {
        compass,
        stations,
        selectedStation,
        inputValue,
        onInputFocus,
        handleSelectSuggestion,
        filteredStations,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .search-origin {
    .input {
      min-height: rem(64);
    }
  }
</style>
